import { AlertTriangle, FileText, X, Check } from 'lucide-react';

interface DuplicateFileInfo {
  fileName: string;
  existingDocument?: {
    title: string;
    fileSize?: number;
    uploadedAt?: string;
    exam?: string;
  };
}

interface DuplicateFileDialogProps {
  isOpen: boolean;
  duplicateInfo: DuplicateFileInfo | null;
  onUseExisting: () => void;
  onCancel: () => void;
}

export function DuplicateFileDialog({
  isOpen,
  duplicateInfo,
  onUseExisting,
  onCancel,
}: DuplicateFileDialogProps) {
  if (!isOpen || !duplicateInfo) return null;

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-full">
                <AlertTriangle className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Duplicate File Detected</h3>
                <p className="text-white/90 mt-1">This file already exists in the system</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File being uploaded */}
          <div>
            <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
              File You're Trying to Upload
            </h4>
            <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <FileText className="w-10 h-10 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{duplicateInfo.fileName}</p>
                <p className="text-sm text-gray-600">New upload</p>
              </div>
            </div>
          </div>

          {/* Existing file */}
          <div>
            <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
              Existing File in System
            </h4>
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
              <FileText className="w-10 h-10 text-yellow-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  {duplicateInfo.existingDocument?.title || 'Existing Document'}
                </p>
                <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                  {duplicateInfo.existingDocument?.exam && (
                    <span className="flex items-center">
                      <span className="font-medium">Exam:</span>
                      <span className="ml-1">{duplicateInfo.existingDocument.exam}</span>
                    </span>
                  )}
                  <span className="flex items-center">
                    <span className="font-medium">Size:</span>
                    <span className="ml-1">{formatFileSize(duplicateInfo.existingDocument?.fileSize)}</span>
                  </span>
                  <span className="flex items-center">
                    <span className="font-medium">Uploaded:</span>
                    <span className="ml-1">{formatDate(duplicateInfo.existingDocument?.uploadedAt)}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Warning message */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Note:</strong> Uploading duplicate files can create confusion and waste storage space.
              We recommend using the existing file instead. If you want to upload a new version, please
              delete the old one first or use a different filename.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-6 py-2.5 rounded-lg font-semibold text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel Upload
          </button>
          <button
            onClick={onUseExisting}
            className="px-6 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl transition-all flex items-center"
          >
            <Check className="w-4 h-4 mr-2" />
            Use Existing File
          </button>
        </div>
      </div>
    </div>
  );
}
